import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cn } from "../lib/cn";
import { useFormSkin } from "./FormSkinContext";
import { InfoCircleIcon } from "./icons";

interface InfoTooltipProps {
  /** The helper content revealed on hover, focus or click: a short string or rich nodes. */
  text: ReactNode;
  /**
   * Accessible name for the trigger button. Defaults to a generic label; pass a
   * field-specific one (e.g. "About preferred language") when several tooltips
   * sit near each other so screen-reader users can tell them apart.
   */
  label?: string | undefined;
  className?: string | undefined;
  /**
   * Extra classes merged last onto the trigger `<button>`, so they win
   * conflicts (e.g. a larger tap target).
   */
  triggerClassName?: string | undefined;
  /** Extra classes merged last onto the `role="tooltip"` panel (e.g. a wider or re-anchored panel). */
  panelClassName?: string | undefined;
  /**
   * Position the panel `fixed` against the trigger's viewport rect, so
   * `overflow` scroll containers (modal bodies, table wrappers) can't clip it.
   * It flips above the trigger when it would run off the viewport bottom.
   * Without it the panel is absolutely positioned.
   */
  fixed?: boolean | undefined;
}

/** The `mt-1.5` gap between trigger and panel, in px. */
const PANEL_GAP = 6;

/**
 * Viewport placement for a `fixed` panel: below the trigger, or above it when below would overflow.
 *
 * It measures the layout viewport (`documentElement.client*`, scrollbars
 * excluded), which is the box `fixed` offsets resolve against. `window.inner*`
 * include the scrollbars and would put the panel off by the scrollbar width on
 * a scrolling page.
 */
function fixedPanelStyle(trigger: DOMRect, panelHeight: number): CSSProperties {
  const { clientWidth, clientHeight } = document.documentElement;
  const below = trigger.bottom + PANEL_GAP + panelHeight <= clientHeight;
  return {
    position: "fixed",
    left: "auto",
    right: clientWidth - trigger.right,
    margin: 0,
    transform: "none",
    // Each branch also neutralises the other edge: the class list carries
    // `top-full`, and an inline `bottom` next to `top: 100%` would collapse the
    // box to its padding and pin it off-screen at the viewport's last pixel.
    ...(below
      ? { top: trigger.bottom + PANEL_GAP, bottom: "auto" }
      : { top: "auto", bottom: clientHeight - trigger.top + PANEL_GAP }),
  };
}

/**
 * Accessible info trigger that reveals `text` on hover, keyboard focus or click, and closes on Escape.
 *
 * The panel forces `whitespace-normal` so a `nowrap` host (e.g. a price span)
 * can't stop its lines from wrapping.
 *
 * The trigger is a real `<button>` with an `aria-label`, and the panel is
 * `role="tooltip"`, referenced via `aria-describedby` while visible. The panel
 * shows while hovered or opened. `onClick` only ever opens it, so the
 * focus-then-click order of a mouse press can't toggle it straight back shut.
 * Escape closes the opened state without a blur.
 *
 * The enroll skin draws `InfoCircleIcon` at `1em`, so the trigger's font-size
 * class sets the glyph size; the styleguide skin shows a text "i".
 */
export function InfoTooltip({
  text,
  label = "More information",
  className,
  triggerClassName,
  panelClassName,
  fixed,
}: InfoTooltipProps): ReactNode {
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const visible = hovered || open;
  const skin = useFormSkin();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const [fixedStyle, setFixedStyle] = useState<CSSProperties>();

  // A `fixed` panel is measured once it is in the DOM, before paint: the
  // trigger's viewport rect + the panel's own height decide below vs. above.
  // Hiding clears the style so the next open is measured under the absolute
  // classes, never under a stale fixed placement.
  useLayoutEffect(() => {
    if (!fixed) return;
    if (!visible || !triggerRef.current || !panelRef.current) {
      setFixedStyle(undefined);
      return;
    }
    setFixedStyle(
      fixedPanelStyle(triggerRef.current.getBoundingClientRect(), panelRef.current.offsetHeight),
    );
  }, [fixed, visible]);

  return (
    <span className={cn("relative inline-flex align-middle", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={visible ? tipId : undefined}
        aria-expanded={visible}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        className={cn(
          "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
          skin === "enroll"
            ? "text-slate-teal"
            : "border border-slate-400 text-[10px] font-semibold italic leading-none text-slate-500",
          "hover:border-ember hover:text-ember transition-colors",
          triggerClassName,
        )}
      >
        {skin === "enroll" ? <InfoCircleIcon size={13} className="size-[1em]" /> : "i"}
      </button>
      {visible && (
        <span
          ref={panelRef}
          role="tooltip"
          id={tipId}
          style={fixed ? fixedStyle : undefined}
          className={cn(
            "absolute left-1/2 top-full z-20 mt-1.5 w-56 -translate-x-1/2",
            "rounded-md border border-slate-200 bg-white px-3 py-2",
            "whitespace-normal text-left text-xs font-normal leading-snug text-slate-700 shadow-lg",
            panelClassName,
          )}
        >
          {text}
        </span>
      )}
    </span>
  );
}
